/**
 * The comparison TARGET as one visual sentence:
 *
 *   COMPARED TO  ( This row | the TOTAL )  [ metric ▼ ]  over  [ window ▼ ]
 *
 * Grouping the three choices in a bordered card makes the right-hand side
 * read left-to-right as one thought instead of four disconnected dropdowns.
 * Switching to a drop-scoped RHS (source_* / list_*) strips a stored
 * `against:"total"` — the backend rejects that pair, so the editor must
 * never hold it either.
 * For Media Devoted | Created By Ciro Autiero
 */

import { type FC } from 'react';
import type { TagCompareTo } from '@trafficpulse/shared';

import { compareWindowOptions, windowValueOf, withWindow } from '../compare-window';
import { useCompareWindowExclusions } from '../window-exclusions';

export interface CompareTargetGroupProps {
  ct: TagCompareTo;
  onPatch: (next: TagCompareTo) => void;
  fields: ReadonlyArray<{ key: string; label: string }>;
  /** Render slot for the This-row/TOTAL toggle (sizes differ per surface). */
  toggle: React.ReactNode;
}

const SELECT =
  'h-9 min-w-0 rounded-md border border-input bg-background px-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring';

/** Per-drop source/list context has no meaningful population TOTAL. */
export const isDropScoped = (field: string): boolean =>
  field.startsWith('source_') || field.startsWith('list_');

/** Metric switch that keeps `against` legal for the new field. */
export const patchComparedField = (
  ct: TagCompareTo,
  field: string,
): TagCompareTo => ({
  ...ct,
  field,
  against: isDropScoped(field) ? undefined : ct.against,
});

export const CompareTargetGroup: FC<CompareTargetGroupProps> = ({
  ct,
  onPatch,
  fields,
  toggle,
}) => {
  const excluded = useCompareWindowExclusions();
  return (
    <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-2 rounded-md border border-sky-500/25 bg-background/60 px-2.5 py-2">
      <span className="w-full text-[11px] font-medium uppercase tracking-wide text-sky-700/80 dark:text-sky-300/80">
        Compared to
      </span>
      {toggle}
      <span className="text-xs text-muted-foreground">’s</span>
      <select
        value={ct.field}
        onChange={(e) => onPatch(patchComparedField(ct, e.target.value))}
        className={`${SELECT} min-w-[120px] flex-1`}
        aria-label="Compared metric"
      >
        {fields.map((f) => (
          <option key={f.key} value={f.key}>{f.label}</option>
        ))}
      </select>
      <span className="text-xs text-muted-foreground">over</span>
      <select
        value={windowValueOf(ct)}
        onChange={(e) => onPatch(withWindow(ct, e.target.value))}
        className={`${SELECT} min-w-[140px]`}
        aria-label="Compare window"
      >
        {compareWindowOptions(excluded).map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    </div>
  );
};

export default CompareTargetGroup;
